"use client";

/**
 * Dashboard → Platform → "Platform at a Glance" (Gordon 2026-11-01: "a little
 * more in-depth behind each glance block. Make it into a button that slides
 * open more in-depth data below, toggles open/close, or closes when you click
 * another dashboard glance value").
 *
 * Six cards; one panel open at a time, sliding open under the whole grid
 * (grid-rows 0fr → 1fr, instant under Reduce Motion). Tracks and Releases use
 * what the page already loaded; Users, Artists, Marketplace and Events are
 * fetched from /api/dashboard/glance the first time their panel opens.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import {
  Users, Music, Disc3, Star, Package, Calendar, ChevronDown, X, ArrowRight, RotateCcw,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/utils";
import { FitNumber } from "@/components/ui/fit-number";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { GenerativeArtistAvatar } from "@/components/media/generative-artist-avatar";
import type { AppStats } from "@/services/stats";
import type { GuestAnalyticsAggregate } from "@/services/guest-analytics";
import {
  summariseReleases, SERVER_GLANCE_SECTIONS,
  type GlanceKey, type GlanceListItem, type MonthCount, type ServerGlance, type ServerGlanceSection, type TracksGlance,
} from "@/lib/dashboard-glance";

/** The Catalogue Intelligence numbers the page computes (subset used here). */
export interface GlanceDeepStats {
  genreBreakdown: { name: string; value: number }[];
  noGenre?: number;
  formatBreakdown: { name: string; value: number }[];
  avgDurationMs: number;
  tracksWithIsrc: number;
  tracksTotal: number;
  tracksWithLyrics: number;
}

export interface PlatformGlanceProps {
  loading: boolean;
  appStats: AppStats | null;
  deepStats: GlanceDeepStats | null;
  catalogueError: boolean;
  tracks: TracksGlance | null;
  releases: Record<string, unknown>[];
  hiddenWorkIds: Set<string>;
  uploadsOverTime: { key?: string; month: string; count: number }[];
  loadingUploads: boolean;
  uploadHistoryError: boolean;
  guestAnalytics: GuestAnalyticsAggregate | null;
  loadingVisitor: boolean;
  signedIn: boolean;
}

type ServerState<K extends ServerGlanceSection> =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; data: ServerGlance[K] };

const CARDS: { key: GlanceKey; icon: React.ElementType; stat: keyof AppStats | null; accent?: boolean }[] = [
  { key: "users", icon: Users, stat: "users" },
  { key: "tracks", icon: Music, stat: "works", accent: true },
  { key: "releases", icon: Disc3, stat: "releases" },
  { key: "artists", icon: Star, stat: "artists" },
  { key: "marketplace", icon: Package, stat: "marketplaceItems" },
  { key: "events", icon: Calendar, stat: "events" },
];
/** AppStats field → the collection name `unavailable` reports. */
const UNAVAILABLE_NAME: Partial<Record<keyof AppStats, string>> = { works: "works", marketplaceItems: "items" };

const isServerSection = (k: GlanceKey): k is ServerGlanceSection =>
  (SERVER_GLANCE_SECTIONS as readonly string[]).includes(k);

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// ─── Formatting ──────────────────────────────────────────────────────────────

function useFormat() {
  const locale = useLocale();
  return useMemo(() => {
    const safe = <T,>(make: () => T, fallback: () => T): T => { try { return make(); } catch { return fallback(); } };
    const num = safe(() => new Intl.NumberFormat(locale), () => new Intl.NumberFormat("en"));
    const compact = safe(() => new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }), () => new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }));
    const pct = safe(() => new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }), () => new Intl.NumberFormat("en", { style: "percent", maximumFractionDigits: 0 }));
    const month = safe(() => new Intl.DateTimeFormat(locale, { month: "short", year: "2-digit", timeZone: "UTC" }), () => new Intl.DateTimeFormat("en", { month: "short", year: "2-digit", timeZone: "UTC" }));
    const day = safe(() => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }), () => new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric" }));
    const event = safe(() => new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }), () => new Intl.DateTimeFormat("en", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }));
    return {
      n: (v: number) => num.format(v),
      compact: (v: number) => compact.format(v),
      /** share of `total`, "—" when there is no total */
      pct: (v: number, total: number) => (total > 0 ? pct.format(v / total) : "—"),
      month: (key: string) => month.format(new Date(`${key}-01T00:00:00Z`)),
      day: (isoDate: string | null) => (isoDate ? day.format(new Date(isoDate)) : ""),
      event: (isoDate: string | null) => (isoDate ? event.format(new Date(isoDate)) : ""),
      money: (v: number, currency: string) => {
        try { return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: v % 1 ? 2 : 0 }).format(v); }
        catch { return `${num.format(v)} ${currency}`; }
      },
      duration: (ms: number) => {
        const s = Math.round(ms / 1000);
        return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
      },
    };
  }, [locale]);
}
type Fmt = ReturnType<typeof useFormat>;

const prettyKey = (k: string) => {
  const s = k.replace(/[-_]+/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
};

// ─── Building blocks ─────────────────────────────────────────────────────────

function Metric({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="border border-border bg-background/40 p-3 min-w-0">
      <p className="text-2xs text-muted-foreground">{label}</p>
      <p className="text-lg sm:text-xl font-bold text-foreground leading-tight tabular-nums break-words">{value}</p>
      {sub && <p className="text-2xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function Metrics({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{children}</div>;
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{children}</h4>;
}

function BarList({ title, items, total, fmt, empty }: {
  title: string;
  items: { label: string; value: number; href?: string }[];
  /** Percentages are of this; defaults to the sum of the rows. */
  total?: number;
  fmt: Fmt;
  empty: string;
}) {
  const max = Math.max(1, ...items.map(i => i.value));
  const sum = total ?? items.reduce((s, i) => s + i.value, 0);
  return (
    <div className="min-w-0">
      <SubHeading>{title}</SubHeading>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {items.map(i => (
            <li key={i.label}>
              <div className="flex items-center justify-between gap-2 text-xs mb-0.5">
                {i.href ? (
                  <Link href={i.href} className="font-medium text-foreground truncate underline-offset-2 hover:underline">{i.label}</Link>
                ) : (
                  <span className="font-medium text-foreground truncate">{i.label}</span>
                )}
                <span className="text-muted-foreground tabular-nums flex-shrink-0">{fmt.n(i.value)} · {fmt.pct(i.value, sum)}</span>
              </div>
              <div className="h-1.5 bg-muted overflow-hidden" aria-hidden="true">
                <div className="h-full bg-primary transition-[width] duration-500 motion-reduce:transition-none" style={{ width: `${(i.value / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MonthChart({ title, note, data, fmt, seriesName, empty }: {
  title: string; note?: string; data: MonthCount[]; fmt: Fmt; seriesName: string; empty: string;
}) {
  const rows = data.map(d => ({ label: fmt.month(d.key), count: d.count }));
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div className="min-w-0">
      <SubHeading>{title}</SubHeading>
      {note && <p className="text-2xs text-muted-foreground -mt-1 mb-2">{note}</p>}
      {/* Screen readers get the numbers as text; the chart is the picture of them. */}
      <p className="sr-only">{rows.map(r => `${r.label}: ${fmt.n(r.count)}`).join(", ")}</p>
      <div aria-hidden="true">
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={rows} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              cursor={{ fill: "hsl(var(--muted)/0.3)" }}
            />
            <Bar dataKey="count" name={seriesName} fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {total === 0 && <p className="text-xs text-muted-foreground mt-1">{empty}</p>}
    </div>
  );
}

function Thumb({ url, name }: { url: string; name: string }) {
  return (
    <div className="w-10 h-10 flex-shrink-0 overflow-hidden bg-muted" style={{ borderRadius: "var(--thumbnail-radius)" }}>
      {url ? (
        <Image src={url} alt="" width={40} height={40} unoptimized className="w-full h-full object-cover" />
      ) : (
        <GenerativeArtistAvatar name={name || "BB"} size={40} className="w-full h-full" />
      )}
    </div>
  );
}

function ItemList({ title, items, empty, dateOf }: {
  title: string; items: GlanceListItem[]; empty: string; dateOf: (iso: string | null) => string;
}) {
  return (
    <div className="min-w-0">
      <SubHeading>{title}</SubHeading>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-1">
          {items.map(i => (
            <li key={i.id}>
              <Link href={i.href} className="flex items-center gap-3 p-1.5 -mx-1.5 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Thumb url={i.imageUrl} name={i.title} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground truncate">{i.title || "—"}</span>
                  <span className="block text-2xs text-muted-foreground truncate">
                    {[i.sub, dateOf(i.date)].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OpenPage({ href, label }: { href: string; label: string }) {
  return (
    <div className="flex justify-end">
      <Button asChild variant="outlineContrast" size="sm">
        <Link href={href}>{label}<ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></Link>
      </Button>
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="space-y-3" aria-hidden="true">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-[4.5rem]" />)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export function PlatformGlance(props: PlatformGlanceProps) {
  const { loading, appStats, deepStats, tracks, releases, hiddenWorkIds } = props;
  const t = useTranslations("dashboardGlance");
  const fmt = useFormat();
  const uid = useId().replace(/:/g, "");
  const panelId = `glance-panel-${uid}`;
  const titleId = `glance-title-${uid}`;

  const [open, setOpen] = useState<GlanceKey | null>(null);
  // What the panel shows — kept while it slides shut so it never collapses empty.
  const [shown, setShown] = useState<GlanceKey | null>(null);
  const [server, setServer] = useState<{ [K in ServerGlanceSection]?: ServerState<K> }>({});
  const inFlight = useRef(new Set<ServerGlanceSection>());
  const cardRefs = useRef<Partial<Record<GlanceKey, HTMLButtonElement | null>>>({});
  const panelRef = useRef<HTMLElement>(null);

  const load = useCallback((section: ServerGlanceSection) => {
    if (inFlight.current.has(section)) return;
    inFlight.current.add(section);
    setServer(s => ({ ...s, [section]: { status: "loading" } }));
    fetch(`/api/dashboard/glance?section=${section}`)
      .then(async r => {
        const data = await r.json().catch(() => null);
        if (!r.ok || !data || typeof data !== "object" || "error" in data) throw new Error("unavailable");
        setServer(s => ({ ...s, [section]: { status: "ready", data } }));
      })
      .catch(() => setServer(s => ({ ...s, [section]: { status: "error" } })))
      .finally(() => inFlight.current.delete(section));
  }, []);

  const toggle = (key: GlanceKey) => {
    const next = open === key ? null : key;
    setOpen(next);
    if (!next) return;
    setShown(next);
    if (isServerSection(next)) {
      const st = server[next];
      if (!st || st.status === "error") load(next);
    }
  };

  const close = () => {
    const key = open;
    setOpen(null);
    if (key) cardRefs.current[key]?.focus();
  };

  // If the panel opens below the fold (cards near the bottom of the screen),
  // bring its top into view once it has slid open.
  useEffect(() => {
    if (!open) return;
    const reduce = prefersReducedMotion();
    const timer = window.setTimeout(() => {
      const el = panelRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      if (top > window.innerHeight - 140) window.scrollBy({ top: top - window.innerHeight / 3, behavior: reduce ? "auto" : "smooth" });
    }, reduce ? 0 : 320);
    return () => window.clearTimeout(timer);
  }, [open]);

  const releasesGlance = useMemo(() => summariseReleases(releases, hiddenWorkIds), [releases, hiddenWorkIds]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" aria-busy="true">
        {CARDS.map(c => <Skeleton key={c.key} className="h-24" />)}
      </div>
    );
  }
  if (!appStats) return null;

  const valueOf = (c: typeof CARDS[number]): string => {
    if (c.key === "tracks" && deepStats) return fmt.compact(deepStats.tracksTotal);
    if (!c.stat) return "—";
    const name = UNAVAILABLE_NAME[c.stat] ?? c.stat;
    if (appStats.unavailable?.includes(name)) return "—";
    const v = appStats[c.stat];
    return typeof v === "number" ? fmt.compact(v) : "—";
  };

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {CARDS.map(c => {
          const active = open === c.key;
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              type="button"
              ref={el => { cardRefs.current[c.key] = el; }}
              data-bg="card"
              aria-expanded={active}
              aria-controls={panelId}
              onClick={() => toggle(c.key)}
              className={cn(
                "border bg-card text-card-foreground shadow-sm p-4 flex flex-col text-left w-full min-w-0 [container-type:inline-size]",
                "transition-all duration-200 motion-reduce:transition-none hover:border-foreground/30",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                c.accent && "border-primary/40 bg-primary/5",
                active && "ring-2 ring-foreground/30 border-foreground/40 bg-foreground/5",
              )}
            >
              {/* Icon beside the label, the number on its own row and sized to fit (Gordon 2026-11-03). */}
              <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                <Icon className={cn("h-3.5 w-3.5 shrink-0", c.accent && "text-primary")} aria-hidden="true" />
                <span className="truncate">{t(`cards.${c.key}`)}</span>
              </span>
              <FitNumber className="mt-1">{valueOf(c)}</FitNumber>
              <span className="min-w-0">
                <span className="block text-xs text-muted-foreground mt-1">{t(`cards.${c.key}Sub`)}</span>
                <span className="mt-1 flex items-center gap-0.5 text-3xs text-muted-foreground">
                  <ChevronDown className={cn("h-3 w-3 transition-transform duration-200 motion-reduce:transition-none", active && "rotate-180")} aria-hidden="true" />
                  {active ? t("hintClose") : t("hintOpen")}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* The slide: grid rows 0fr → 1fr animates to the content's own height. */}
      <div
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="min-h-0 overflow-hidden" inert={!open}>
          <section
            ref={panelRef}
            id={panelId}
            aria-labelledby={titleId}
            data-bg="card"
            onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); close(); } }}
            className="mt-3 border bg-card text-card-foreground shadow-sm p-4 sm:p-5"
          >
            <div className="flex items-center justify-between gap-3 mb-4">
              <h3 id={titleId} className="text-base font-semibold text-foreground">
                {shown ? t("panelTitle", { name: t(`cards.${shown}`) }) : ""}
              </h3>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={close} aria-label={t("close")}>
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            {shown && (
              <div key={shown} className="space-y-5 animate-in fade-in-0 duration-300 motion-reduce:animate-none">
                <PanelBody section={shown} {...props} releasesGlance={releasesGlance} server={server} onRetry={load} fmt={fmt} />
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ─── Panels ──────────────────────────────────────────────────────────────────

type BodyProps = PlatformGlanceProps & {
  section: GlanceKey;
  releasesGlance: ReturnType<typeof summariseReleases>;
  server: { [K in ServerGlanceSection]?: ServerState<K> };
  onRetry: (s: ServerGlanceSection) => void;
  fmt: Fmt;
};

function ServerGate<K extends ServerGlanceSection>({ section, state, onRetry, children }: {
  section: K; state: ServerState<K> | undefined; onRetry: (s: ServerGlanceSection) => void; children: (data: ServerGlance[K]) => React.ReactNode;
}) {
  const t = useTranslations("dashboardGlance");
  if (!state || state.status === "loading") {
    return (
      <div role="status">
        <span className="sr-only">{t("loading")}</span>
        <PanelSkeleton />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-foreground">
        <span>{t("error")}</span>
        <Button type="button" variant="outlineContrast" size="sm" onClick={() => onRetry(section)}>
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> {t("retry")}
        </Button>
      </div>
    );
  }
  return <>{children(state.data)}</>;
}

function PanelBody(p: BodyProps) {
  const t = useTranslations("dashboardGlance");
  const { fmt } = p;

  switch (p.section) {
    // ── Users ──
    case "users":
      return (
        <ServerGate section="users" state={p.server.users} onRetry={p.onRetry}>
          {u => {
            const accounts = p.appStats && !p.appStats.unavailable?.includes("users") ? p.appStats.users : u.profiles;
            const userData = p.guestAnalytics?.user ?? {};
            const pages = Object.entries(userData.pages ?? {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
            const pageTotal = Object.values(userData.pages ?? {}).reduce((s, v) => s + v, 0);
            const hasActivity = (p.guestAnalytics?.userTotalPageViews ?? 0) > 0;
            return (
              <>
                <Metrics>
                  <Metric label={t("users.accounts")} value={fmt.n(accounts)} sub={t("users.accountsSub")} />
                  <Metric label={t("users.new30")} value={fmt.n(u.new30)} />
                  <Metric
                    label={t("users.active30")}
                    value={u.active30 == null ? "—" : fmt.n(u.active30)}
                    sub={u.active7 == null ? undefined : t("users.active7", { n: fmt.n(u.active7) })}
                  />
                  <Metric label={t("users.placeholders")} value={fmt.n(u.placeholders)} sub={t("users.placeholdersSub")} />
                </Metrics>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <BarList
                    title={t("users.byType")}
                    items={u.byKind.map(k => ({ label: t(`users.kind.${k.key}`), value: k.value }))}
                    fmt={fmt}
                    empty={t("empty")}
                  />
                  <MonthChart
                    title={t("users.signups")}
                    note={u.signupsSource === "auth" ? t("users.signupsAuth") : t("users.signupsProfiles")}
                    data={u.signups}
                    fmt={fmt}
                    seriesName={t("users.signupsSeries")}
                    empty={t("empty")}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="min-w-0">
                    <SubHeading>{t("users.activity")}</SubHeading>
                    {p.loadingVisitor ? <Skeleton className="h-16" /> : !hasActivity ? (
                      <p className="text-xs text-muted-foreground">{t("users.noActivity")}</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <Metric label={t("users.pageViews")} value={fmt.n(p.guestAnalytics?.userTotalPageViews ?? 0)} />
                        <Metric label={t("users.sessions")} value={fmt.n(p.guestAnalytics?.userTotalSessions ?? 0)} />
                      </div>
                    )}
                  </div>
                  {!p.loadingVisitor && hasActivity && (
                    <BarList
                      title={t("users.topPages")}
                      items={pages.map(([k, v]) => ({ label: `/${k}`, value: v }))}
                      total={pageTotal}
                      fmt={fmt}
                      empty={t("empty")}
                    />
                  )}
                </div>
                <OpenPage href="/people" label={t("open", { page: t("pages.people") })} />
              </>
            );
          }}
        </ServerGate>
      );

    // ── Tracks ──
    case "tracks": {
      if (p.catalogueError) return <p role="alert" className="text-sm text-foreground">{t("error")}</p>;
      const d = p.deepStats;
      if (!d) return <p className="text-sm text-muted-foreground">{t("empty")}</p>;
      const tg = p.tracks;
      return (
        <>
          <Metrics>
            <Metric label={t("tracks.isrc")} value={fmt.pct(d.tracksWithIsrc, d.tracksTotal)} sub={t("ofTotal", { n: fmt.n(d.tracksWithIsrc), total: fmt.n(d.tracksTotal) })} />
            <Metric label={t("tracks.lyrics")} value={fmt.pct(d.tracksWithLyrics, d.tracksTotal)} sub={t("ofTotal", { n: fmt.n(d.tracksWithLyrics), total: fmt.n(d.tracksTotal) })} />
            <Metric label={t("tracks.explicit")} value={tg ? fmt.pct(tg.explicit, d.tracksTotal) : "—"} sub={tg ? t("tracks.explicitSub", { n: fmt.n(tg.explicit) }) : undefined} />
            <Metric label={t("tracks.avgLength")} value={d.avgDurationMs > 0 ? fmt.duration(d.avgDurationMs) : "—"} sub={tg ? t("tracks.analysed", { n: fmt.n(tg.analysed) }) : undefined} />
          </Metrics>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {p.loadingUploads ? <Skeleton className="h-48" /> : p.uploadHistoryError ? (
              <p role="alert" className="text-sm text-foreground">{t("error")}</p>
            ) : (
              <MonthChart
                title={t("tracks.uploads")}
                note={t("tracks.uploadsNote")}
                data={p.uploadsOverTime.filter(m => m.key).map(m => ({ key: m.key!, count: m.count }))}
                fmt={fmt}
                seriesName={t("tracks.uploadsSeries")}
                empty={t("empty")}
              />
            )}
            <div className="space-y-5 min-w-0">
              <div>
                <BarList
                  title={t("tracks.genres")}
                  items={d.genreBreakdown.slice(0, 6).map(g => ({ label: g.name, value: g.value }))}
                  total={d.tracksTotal}
                  fmt={fmt}
                  empty={t("empty")}
                />
                {(d.noGenre ?? 0) > 0 && (
                  <p className="text-2xs text-muted-foreground mt-2">{t("tracks.noGenre", { n: fmt.n(d.noGenre ?? 0) })}</p>
                )}
              </div>
              {/* A track counts once per stored format (master + delivery copy). */}
              <BarList
                title={t("tracks.formats")}
                items={d.formatBreakdown.slice(0, 6).map(f => ({ label: f.name, value: f.value }))}
                total={d.tracksTotal}
                fmt={fmt}
                empty={t("empty")}
              />
            </div>
          </div>
          {p.signedIn
            ? <OpenPage href="/media" label={t("open", { page: t("pages.mediaVault") })} />
            : <OpenPage href="/releases" label={t("open", { page: t("pages.releases") })} />}
        </>
      );
    }

    // ── Releases ──
    case "releases": {
      const r = p.releasesGlance;
      const total = p.appStats && !p.appStats.unavailable?.includes("releases") ? p.appStats.releases : r.total;
      if (r.total === 0) return <p className="text-sm text-muted-foreground">{t("empty")}</p>;
      return (
        <>
          <Metrics>
            <Metric label={t("releases.total")} value={fmt.n(total)} />
            <Metric label={t("releases.avgTracks")} value={r.avgTracks.toFixed(1)} />
            <Metric label={t("releases.withCover")} value={fmt.pct(r.withCover, r.total)} sub={t("ofTotal", { n: fmt.n(r.withCover), total: fmt.n(r.total) })} />
            <Metric label={t("releases.playable")} value={fmt.n(r.playable)} sub={t("releases.playableSub")} />
          </Metrics>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <BarList
              title={t("releases.byType")}
              items={r.byKind.map(k => ({ label: t(`releases.kind.${k.key}`), value: k.value }))}
              fmt={fmt}
              empty={t("empty")}
            />
            <ItemList title={t("releases.newest")} items={r.newest} empty={t("empty")} dateOf={fmt.day} />
          </div>
          <OpenPage href="/releases" label={t("open", { page: t("pages.releases") })} />
        </>
      );
    }

    // ── Artists ──
    case "artists":
      return (
        <ServerGate section="artists" state={p.server.artists} onRetry={p.onRetry}>
          {a => (
            <>
              <Metrics>
                <Metric label={t("artists.total")} value={fmt.n(a.total)} sub={a.hidden > 0 ? t("artists.hidden", { n: fmt.n(a.hidden) }) : undefined} />
                <Metric label={t("artists.claimed")} value={fmt.n(a.claimed)} sub={fmt.pct(a.claimed, a.total)} />
                <Metric label={t("artists.unclaimed")} value={fmt.n(a.unclaimed)} sub={t("artists.unclaimedSub")} />
                <Metric label={t("artists.withPhoto")} value={fmt.n(a.withPhoto)} sub={t("artists.withoutPhoto", { n: fmt.n(a.withoutPhoto) })} />
              </Metrics>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {p.tracks ? (
                  <BarList
                    title={t("artists.topByTracks")}
                    items={p.tracks.topArtists.map(x => ({ label: x.name, value: x.tracks, href: x.href }))}
                    total={p.deepStats?.tracksTotal}
                    fmt={fmt}
                    empty={t("empty")}
                  />
                ) : p.catalogueError ? (
                  <p role="alert" className="text-sm text-foreground">{t("error")}</p>
                ) : <Skeleton className="h-40" />}
                <BarList
                  title={t("artists.completeness")}
                  items={[
                    { label: t("artists.claimedBar"), value: a.claimed },
                    { label: t("artists.photoBar"), value: a.withPhoto },
                  ]}
                  total={a.total}
                  fmt={fmt}
                  empty={t("empty")}
                />
              </div>
              <OpenPage href="/artists" label={t("open", { page: t("pages.artists") })} />
            </>
          )}
        </ServerGate>
      );

    // ── Marketplace ──
    case "marketplace":
      return (
        <ServerGate section="marketplace" state={p.server.marketplace} onRetry={p.onRetry}>
          {m => {
            const pwyw = m.byPricing.find(x => x.key === "pwyw")?.value ?? 0;
            const free = m.byPricing.find(x => x.key === "free")?.value ?? 0;
            return (
              <>
                <Metrics>
                  <Metric label={t("marketplace.listed")} value={fmt.n(m.listed)} sub={t("marketplace.notListed", { n: fmt.n(m.notListed) })} />
                  <Metric label={t("marketplace.pwyw")} value={fmt.pct(pwyw, m.listed)} sub={t("marketplace.pwywSub", { n: fmt.n(pwyw) })} />
                  <Metric label={t("marketplace.free")} value={fmt.n(free)} />
                  <Metric
                    label={t("marketplace.priceRange")}
                    value={m.price ? `${fmt.money(m.price.min, m.price.currency)}–${fmt.money(m.price.max, m.price.currency)}` : "—"}
                    sub={m.price ? t("marketplace.median", { price: fmt.money(m.price.median, m.price.currency) }) : undefined}
                  />
                </Metrics>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <BarList
                    title={t("marketplace.byCategory")}
                    items={m.byCategory.map(c => ({ label: prettyKey(c.key), value: c.value }))}
                    total={m.listed}
                    fmt={fmt}
                    empty={t("empty")}
                  />
                  <BarList
                    title={t("marketplace.byPricing")}
                    items={m.byPricing.map(c => ({ label: t(`marketplace.pricing.${c.key}`), value: c.value }))}
                    total={m.listed}
                    fmt={fmt}
                    empty={t("empty")}
                  />
                </div>
                <ItemList title={t("marketplace.newest")} items={m.newest.map(i => ({ ...i, sub: prettyKey(i.sub) }))} empty={t("empty")} dateOf={fmt.day} />
                <OpenPage href="/marketplace" label={t("open", { page: t("pages.marketplace") })} />
              </>
            );
          }}
        </ServerGate>
      );

    // ── Events ──
    case "events":
      return (
        <ServerGate section="events" state={p.server.events} onRetry={p.onRetry}>
          {e => (
            <>
              <Metrics>
                <Metric label={t("events.upcoming")} value={fmt.n(e.upcoming)} />
                <Metric label={t("events.past")} value={fmt.n(e.past)} sub={e.undated > 0 ? t("events.undated", { n: fmt.n(e.undated) }) : undefined} />
                <Metric label={t("events.bbMade")} value={fmt.n(e.bbMade)} sub={t("events.bbMadeSub")} />
                <Metric label={t("events.imported")} value={fmt.n(e.imported)} sub={t("events.importedSub")} />
              </Metrics>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <ItemList title={t("events.next")} items={e.next} empty={t("events.noneUpcoming")} dateOf={fmt.event} />
                <BarList
                  title={t("events.byCategory")}
                  items={e.byCategory.map(c => ({ label: prettyKey(c.key), value: c.value }))}
                  total={e.total}
                  fmt={fmt}
                  empty={t("empty")}
                />
              </div>
              <OpenPage href="/events" label={t("open", { page: t("pages.events") })} />
            </>
          )}
        </ServerGate>
      );

    default:
      return null;
  }
}
